/**
 * Keeps <html lang> in step with the active UI language.
 *
 * index.html ships a static lang attribute, but the UI language is a user
 * setting (appStore `settings.language`, Russian by default) that can change
 * at runtime: the Appearance switch, settings loaded from the server after
 * sign-in, an import, or a reset. Screen readers, hyphenation, spell-check
 * and the browser's translate prompt all read the document language, so a
 * Russian UI under lang="en" is read out with English pronunciation.
 */
import type { Language } from './index';

/** The product default — must match appStore `defaultSettings.language` and index.html. */
export const DEFAULT_LANGUAGE: Language = 'ru';

/**
 * Map a stored setting to the language the UI actually renders. Mirrors
 * `useTranslation`/`getTranslations`: only 'en' selects English, anything
 * else (missing, empty, unknown) falls back to Russian.
 */
export function resolveLanguage(value: unknown): Language {
  return value === 'en' ? 'en' : DEFAULT_LANGUAGE;
}

export function applyDocumentLang(language: Language, doc: Document = document): void {
  // The store notifies on every state change (each streamed message, too),
  // so skip the write when nothing changed: setting an attribute to its
  // current value still queues a DOM mutation record. Equivalent mutant:
  // dropping this check changes no observable lang value, only the number
  // of redundant writes, so no test can kill it.
  if (doc.documentElement.lang !== language) {
    doc.documentElement.lang = language;
  }
}

interface LanguageState {
  settings?: { language?: unknown };
}

interface LanguageStore {
  getState: () => LanguageState;
  subscribe: (listener: (state: LanguageState) => void) => () => void;
}

/**
 * Apply the store's current language now and on every later change.
 * Returns the unsubscribe function.
 */
export function bindDocumentLang(store: LanguageStore, doc: Document = document): () => void {
  applyDocumentLang(resolveLanguage(store.getState().settings?.language), doc);
  return store.subscribe((state) => {
    applyDocumentLang(resolveLanguage(state.settings?.language), doc);
  });
}
